import { type ReactNode, useState } from "react";
import { DFY_PACKAGE_CREDITS } from "@repo/api/utils/dfy-billing";
import { Button } from "@/lib/components/Button/Button";
import VideoThumbnail from "@/lib/components/VideoThumbnail";
import { useSelfQuery } from "@/lib/queries/user-queries";

const EXPLAINER_THUMBNAIL_SRC = "/dfy-explainer.jpg";

const STARTING_PRICE_USD =
  Math.min(...Object.values(DFY_PACKAGE_CREDITS)) / 100;

const TITLE = (
  <>
    Get videos in{" "}
    <span className="bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
      24 hours.
    </span>{" "}
    Done for you, from ${STARTING_PRICE_USD}
  </>
);

type EmptyStateContent = {
  videoEmbedUrl: string;
  videoTitle: string;
  title: ReactNode;
  description: ReactNode;
  highlights: { label: string; dotClassName: string }[];
  ctaLabel: string;
  testimonials: Testimonial[];
};

type Testimonial = {
  quote: string;
  name: string;
  role: string;
  avatarSrc: string;
};

const PHOTOGRAPHER_CONTENT: EmptyStateContent = {
  videoEmbedUrl:
    "https://player.vimeo.com/video/1233636672?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1",
  videoTitle: "Done For You Photographers",
  title: TITLE,
  description:
    "Add professional listing videos to every shoot and increase your average order value without lifting a finger.",
  highlights: [
    {
      label: "Created by professional editors",
      dotClassName: "bg-primary-400",
    },
    { label: "Resell at your own price", dotClassName: "bg-secondary-400" },
    { label: "Free edits until it's right", dotClassName: "bg-tertiary-400" },
  ],
  ctaLabel: "Submit your photos",
  testimonials: [
    {
      quote:
        "The quality of the renders is outstanding and for the untrained eye looks like a real video clip.'",
      name: "Kelvin Hughes",
      role: "Photographer",
      avatarSrc: "/kelvin-hughes.jpeg",
    },
    {
      quote:
        "Oh amazing I'm so relieved, thank you. I'm just very glad I decided to try this out. Lifesaver",
      name: "Stefan Mare",
      role: "Photographer",
      avatarSrc: "/magnus-media.jpeg",
    },
  ],
};

const AGENT_CONTENT: EmptyStateContent = {
  videoEmbedUrl:
    "https://player.vimeo.com/video/1233639081?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1",
  videoTitle: "Done For You Agents",
  title: TITLE,
  description:
    "Send the listing photos, and a human editor returns three finished videos, MLS-safe and social-ready. No subscription or videographer to book.",
  highlights: [
    {
      label: "Created by professional editors",
      dotClassName: "bg-primary-400",
    },
    { label: "MLS and social formats", dotClassName: "bg-secondary-400" },
    { label: "Free edits until it's right", dotClassName: "bg-tertiary-400" },
  ],
  ctaLabel: "Submit your listing",
  testimonials: [
    {
      quote:
        "Super quick service and very happy with the output, will use again.",
      name: "Andrew Kay",
      role: "Licensed Sales Person",
      avatarSrc: "/adrew-kay.jpeg",
    },
    {
      quote:
        "The ease and quality of getting the video created. Also, very quick turnaround time.",
      name: "Mark Geyer",
      role: "Real Estate Advisor",
      avatarSrc: "/mark-geyer.jpeg",
    },
  ],
};

export default function DfyOrdersEmptyState({
  onCreateOrder,
  isCreatingOrder,
}: {
  onCreateOrder: () => void;
  isCreatingOrder: boolean;
}) {
  const { data: self } = useSelfQuery();
  const content =
    self?.onboardingSurveyRole === "PHOTOGRAPHER"
      ? PHOTOGRAPHER_CONTENT
      : AGENT_CONTENT;

  return (
    <div className="flex flex-1 flex-col items-center px-4 pb-8 text-center sm:px-6">
      <div className="flex w-full flex-1 flex-col items-center justify-center py-6">
        <div className="relative w-full max-w-[min(28svh*16/9,28.75rem)] shrink-0">
          <ExplainerVideo
            key={content.videoEmbedUrl}
            embedUrl={content.videoEmbedUrl}
            title={content.videoTitle}
          />
        </div>

        <h2 className="relative mt-[clamp(1.25rem,4svh,3rem)] text-2xl font-bold text-text-primary sm:text-[26px]">
          {content.title}
        </h2>
        <p className="relative mt-3 max-w-165 text-base text-text-secondary">
          {content.description}
        </p>

        <ul className="relative mt-[clamp(1rem,3svh,2rem)] flex flex-wrap justify-center gap-2 sm:gap-3">
          {content.highlights.map((highlight) => (
            <li
              key={highlight.label}
              className="flex items-center gap-1.5 rounded-full border border-border bg-white px-2.5 py-0.5 text-xs font-medium text-text-secondary sm:px-3 sm:py-1 sm:text-[13px]"
            >
              <span
                aria-hidden="true"
                className={`size-1.5 shrink-0 rounded-full sm:size-1.75 ${highlight.dotClassName}`}
              />
              {highlight.label}
            </li>
          ))}
        </ul>

        <Button
          className="relative mt-[clamp(1.25rem,4svh,3rem)] shrink-0"
          isLoading={isCreatingOrder}
          onClick={onCreateOrder}
        >
          {content.ctaLabel}
        </Button>
      </div>

      <div className="mt-12 w-full max-w-110 shrink-0 lg:mt-0 lg:max-w-4xl">
        <Testimonials
          key={content.videoTitle}
          testimonials={content.testimonials}
        />
      </div>
    </div>
  );
}

function Testimonials({ testimonials }: { testimonials: Testimonial[] }) {
  return (
    <section
      aria-label="Customer testimonials"
      className="grid gap-4 lg:grid-cols-2"
    >
      {testimonials.map((testimonial) => (
        <TestimonialCard key={testimonial.name} testimonial={testimonial} />
      ))}
    </section>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <figure className="rounded-xl border border-grey-200/50 bg-grey-100/50 p-5 text-left">
      <blockquote className="text-base leading-relaxed text-text-primary">
        “{testimonial.quote}”
      </blockquote>
      <figcaption className="mt-3 flex items-center gap-3 text-sm">
        <img
          src={testimonial.avatarSrc}
          alt=""
          className="size-10 shrink-0 rounded-full object-cover"
        />
        <div>
          <div className="font-semibold text-text-primary">
            {testimonial.name}
          </div>
          <div className="text-text-secondary">{testimonial.role}</div>
        </div>
      </figcaption>
    </figure>
  );
}

function ExplainerVideo({
  embedUrl,
  title,
}: {
  embedUrl: string;
  title: string;
}) {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="relative isolate z-10 aspect-video w-full transform-gpu overflow-hidden rounded-2xl border border-border">
      {isPlaying ? (
        <iframe
          className="absolute inset-0 size-full border-0"
          src={embedUrl}
          title={title}
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          aria-label="Play the Done-for-You explainer video"
          onClick={() => setIsPlaying(true)}
          className="group absolute inset-0 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white"
        >
          <VideoThumbnail
            title="Done-for-You explained"
            imageSrc={EXPLAINER_THUMBNAIL_SRC}
            minutes={1}
            className="size-full rounded-none"
          />
        </button>
      )}
    </div>
  );
}
